import { useState, useRef, useEffect, useCallback, ReactNode } from "react";
import { ChevronLeft, ChevronRight, ChevronsDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ResizablePanelsProps {
  leftPanel: ReactNode;
  rightPanel: ReactNode;
  leftTitle?: string;
  rightTitle?: string;
  leftActionBar?: ReactNode;
  defaultLeftCollapsed?: boolean;
  defaultRightCollapsed?: boolean;
  minLeftWidth?: number;
  minRightWidth?: number;
}

export default function ResizablePanels({
  leftPanel,
  rightPanel,
  leftTitle,
  rightTitle,
  leftActionBar,
  defaultLeftCollapsed = false,
  defaultRightCollapsed = false,
}: ResizablePanelsProps) {
  const [leftCollapsed, setLeftCollapsed] = useState(defaultLeftCollapsed);
  const [rightCollapsed, setRightCollapsed] = useState(defaultRightCollapsed);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const leftContentRef = useRef<HTMLDivElement>(null);

  const checkScroll = useCallback(() => {
    const el = leftContentRef.current;
    if (!el) {
      setShowScrollIndicator(false);
      return;
    }
    const hasOverflow = el.scrollHeight > el.clientHeight + 1;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 30;
    setShowScrollIndicator(hasOverflow && !nearBottom);
  }, []);

  useEffect(() => {
    const el = leftContentRef.current;
    if (!el || leftCollapsed) return;

    checkScroll();
    el.addEventListener("scroll", checkScroll);
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", checkScroll);
      resizeObserver.disconnect();
    };
  }, [leftCollapsed, checkScroll]);

  const getWidths = () => {
    if (leftCollapsed && rightCollapsed) return { left: "50%", right: "50%" };
    if (leftCollapsed) return { left: "10%", right: "90%" };
    if (rightCollapsed) return { left: "90%", right: "10%" };
    return { left: "60%", right: "40%" };
  };

  const widths = getWidths();

  return (
    <div className="flex w-full h-full gap-4">
      <div
        className={cn(
          "transition-all duration-300 ease-in-out relative bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col",
          leftCollapsed ? "overflow-hidden" : "",
        )}
        style={{ width: widths.left }}
      >
        {leftTitle && !leftCollapsed && (
          <div className="relative">
            <div className="h-1 bg-gradient-to-r from-purple-600 via-pink-500 to-blue-600" />
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
              <h3 className="text-lg font-semibold text-gray-900">{leftTitle}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setLeftCollapsed(true)}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <div className="relative flex-1 min-h-0">
          <div
            ref={leftContentRef}
            className={cn(
              "h-full px-6 py-4 overflow-y-auto",
              leftCollapsed && "hidden",
            )}
          >
            {leftPanel}
          </div>

          {showScrollIndicator && !leftCollapsed && (
            <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
              <div className="h-16 bg-gradient-to-t from-white to-transparent" />
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 text-xs text-gray-500">
                <ChevronsDown className="h-3 w-3 animate-bounce" />
                <span>Scroll for more</span>
              </div>
            </div>
          )}
        </div>

        {leftActionBar && !leftCollapsed && (
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            {leftActionBar}
          </div>
        )}

        {leftCollapsed && (
          <div
            className="h-full flex items-center justify-center bg-gradient-to-r from-purple-50 to-blue-50 border-r border-gray-200 cursor-pointer hover:from-purple-100 hover:to-blue-100 transition-colors"
            onClick={() => setLeftCollapsed(false)}
          >
            <div className="flex flex-col items-center gap-3 py-4">
              <ChevronRight className="h-5 w-5 text-gray-700" />
              {leftTitle && (
                <span
                  className="text-lg font-semibold text-gray-900"
                  style={{ writingMode: "vertical-rl" }}
                >
                  {leftTitle}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className={cn(
          "transition-all duration-300 ease-in-out relative bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col",
          rightCollapsed ? "overflow-hidden" : "",
        )}
        style={{ width: widths.right }}
      >
        {rightTitle && !rightCollapsed && (
          <div className="relative">
            <div className="h-1 bg-gradient-to-r from-blue-600 via-pink-500 to-purple-600" />
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
              <h3 className="text-lg font-semibold text-gray-900">{rightTitle}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setRightCollapsed(true)}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <div
          className={cn(
            "flex-1 p-6 overflow-y-auto",
            rightCollapsed && "hidden",
          )}
        >
          {rightPanel}
        </div>

        {rightCollapsed && (
          <div
            className="h-full flex items-center justify-center bg-gradient-to-r from-blue-50 to-purple-50 border-l border-gray-200 cursor-pointer hover:from-blue-100 hover:to-purple-100 transition-colors"
            onClick={() => setRightCollapsed(false)}
          >
            <div className="flex flex-col items-center gap-3 py-4">
              <ChevronLeft className="h-5 w-5 text-gray-700" />
              {rightTitle && (
                <span
                  className="text-lg font-semibold text-gray-900"
                  style={{ writingMode: "vertical-rl" }}
                >
                  {rightTitle}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
